import { useEffect, useState, useRef, useCallback } from 'react';
import { User as UserIcon, Sliders, Server, Save, Eye, EyeOff, LayoutDashboard, Plus, Trash2, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, X, Search, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { isPasswordValid, PASSWORD_POLICY_MESSAGE } from '@/lib/passwordPolicy';
import { useAuthStore } from '../../store/useAuthStore';
import { authApi, adminApi, usersApi, examsApi, homepageSectionsApi, homepageSectionsImportApi, type HomepageSectionData, type HomepageSectionPayload } from '../../services/api';

interface Profile {
  id: string;
  name: string;
  email: string;
  role: string;
  avatarUrl?: string;
  isEmailVerified?: boolean;
  totalXp?: number;
  level?: number;
  streak?: number;
  createdAt?: string;
}

interface LimitsResponse {
  limits: {
    freeDailyPracticeLimit: number;
    freeDailySimulationLimit: number;
    guestExamLimit: number;
    fullExamDurationMinutes: number;
    fullExamQuestionsCount: number;
  };
  system: {
    nodeEnv: string;
    nodeVersion: string;
    uptimeSeconds: number;
    memoryMb: number;
  };
}

const formatUptime = (seconds: number) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}j`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}min`);
  return parts.join(' ');
};

export function SettingsPage() {
  const setUser = useAuthStore((s) => s.setUser);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [limits, setLimits] = useState<LimitsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Editable limits state
  const [limitForm, setLimitForm] = useState<LimitsResponse['limits'] | null>(null);
  const [savingLimits, setSavingLimits] = useState(false);

  // Homepage sections state
  const [sections, setSections] = useState<HomepageSectionData[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [profileRes, limitsRes, sectionsRes] = await Promise.all([
          authApi.getProfile(),
          adminApi.getLimits(),
          homepageSectionsApi.getAll(),
        ]);
        if (cancelled) return;
        setProfile(profileRes.data);
        setName(profileRes.data.name ?? '');
        setLimits(limitsRes.data);
        setLimitForm(limitsRes.data.limits);
        setSections(sectionsRes.data);
      } catch {
        if (!cancelled) toast.error('Erreur lors du chargement des paramètres');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSectionsLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    if (!name.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    if (password && !isPasswordValid(password)) {
      toast.error(PASSWORD_POLICY_MESSAGE);
      return;
    }
    setSaving(true);
    try {
      const payload: { name?: string; password?: string } = {};
      if (name.trim() !== profile.name) payload.name = name.trim();
      if (password) payload.password = password;

      if (Object.keys(payload).length === 0) {
        toast.info('Aucune modification à enregistrer');
        return;
      }

      const { data } = await usersApi.update(payload);
      setProfile((p) => p ? { ...p, name: data.name ?? p.name } : p);
      setUser({
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        totalXp: data.totalXp,
        level: data.level,
      });
      setPassword('');
      toast.success('Profil mis à jour');
    } catch {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setSaving(false);
    }
  };

  const setLimitField = (key: keyof LimitsResponse['limits'], raw: string) => {
    const n = raw === '' ? 0 : parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) return;
    setLimitForm((f) => f ? { ...f, [key]: n } : f);
  };

  const handleSaveLimits = async () => {
    if (!limits || !limitForm) return;
    const changes: Partial<LimitsResponse['limits']> = {};
    (Object.keys(limitForm) as (keyof LimitsResponse['limits'])[]).forEach((k) => {
      if (limitForm[k] !== limits.limits[k]) changes[k] = limitForm[k];
    });
    if (Object.keys(changes).length === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }
    setSavingLimits(true);
    try {
      const { data } = await adminApi.updateLimits(changes);
      setLimits((prev) => prev ? { ...prev, limits: data.limits } : prev);
      setLimitForm(data.limits);
      toast.success('Limites mises à jour');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Erreur lors de la mise à jour des limites');
    } finally {
      setSavingLimits(false);
    }
  };

  const limitsDirty = limits && limitForm
    ? (Object.keys(limitForm) as (keyof LimitsResponse['limits'])[]).some((k) => limitForm[k] !== limits.limits[k])
    : false;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Paramètres</h1>
        <p className="text-gray-500 dark:text-gray-400">Profil admin, limites de l'application et informations système.</p>
      </div>

      {/* Profile */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">
            <UserIcon size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mon profil admin</h2>
            <p className="text-xs text-gray-500">Modifier votre nom ou mot de passe.</p>
          </div>
        </div>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nom</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Email (lecture seule)</label>
              <input
                type="email"
                value={profile?.email ?? ''}
                disabled
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Rôle</label>
              <input
                type="text"
                value={profile?.role ?? ''}
                disabled
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-500 cursor-not-allowed"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nouveau mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Laisser vide pour ne pas changer"
                  className="w-full px-3 py-2 pr-10 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              <Save size={16} />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </section>

      {/* Limits */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
            <Sliders size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Limites de l'application</h2>
            <p className="text-xs text-gray-500">Modifiables — sauvegardées en base et appliquées dynamiquement à tous les utilisateurs.</p>
          </div>
        </div>

        {limitForm && (
          <>
            {/* Mode gratuit (Freemium) : ces quotas pilotent le bandeau
                "Mode gratuit : il vous reste X pratiques et Y simulation" et
                sont servis à tous les visiteurs via /public/config. */}
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Mode gratuit (Freemium)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LimitInput
                label="Pratiques gratuites / jour"
                value={limitForm.freeDailyPracticeLimit}
                onChange={(v) => setLimitField('freeDailyPracticeLimit', v)}
                min={0} max={1000}
              />
              <LimitInput
                label="Simulations gratuites / jour"
                value={limitForm.freeDailySimulationLimit}
                onChange={(v) => setLimitField('freeDailySimulationLimit', v)}
                min={0} max={100}
              />
              <LimitInput
                label="Examens invités (sans compte)"
                value={limitForm.guestExamLimit}
                onChange={(v) => setLimitField('guestExamLimit', v)}
                min={0} max={100}
              />
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2 mt-5">Examen complet</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <LimitInput
                label="Durée examen complet (min)"
                value={limitForm.fullExamDurationMinutes}
                onChange={(v) => setLimitField('fullExamDurationMinutes', v)}
                min={5} max={600}
              />
              <LimitInput
                label="Nb questions examen complet"
                value={limitForm.fullExamQuestionsCount}
                onChange={(v) => setLimitField('fullExamQuestionsCount', v)}
                min={1} max={500}
              />
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="button"
                onClick={handleSaveLimits}
                disabled={savingLimits || !limitsDirty}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                <Save size={16} />
                {savingLimits ? 'Enregistrement...' : 'Enregistrer les limites'}
              </button>
            </div>
          </>
        )}
      </section>

      {/* Homepage Sections */}
      <HomepageSectionsPanel
        sections={sections}
        loading={sectionsLoading}
        onChange={setSections}
      />

      {/* System */}
      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-purple-600">
            <Server size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Infos système</h2>
            <p className="text-xs text-gray-500">État du serveur backend.</p>
          </div>
        </div>

        {limits && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <LimitRow label="Environnement" value={limits.system.nodeEnv} />
            <LimitRow label="Version Node.js" value={limits.system.nodeVersion} />
            <LimitRow label="Uptime serveur" value={formatUptime(limits.system.uptimeSeconds)} />
            <LimitRow label="Mémoire utilisée" value={`${limits.system.memoryMb} MB`} />
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Homepage Sections Panel ─────────────────────────────────────────────────

const COLOR_OPTIONS = ['Orange', 'Emerald', 'Blue', 'Purple', 'Pink', 'Yellow', 'Cyan', 'Red'];

const BLANK_SECTION: Omit<HomepageSectionPayload, 'title' | 'slug'> & { title: string; slug: string } = {
  title: '',
  titleEn: '',
  slug: '',
  description: '',
  descriptionEn: '',
  icon: '',
  color: 'Orange',
  isVisible: true,
  featuredExamIds: [],
};

interface SectionFormState extends HomepageSectionPayload {
  id?: string;
  saving?: boolean;
}

function slugify(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface AdminExam {
  id: string;
  name: string;
  icon: string;
  bgGradient: string;
  totalQuestions: number;
}

function HomepageSectionsPanel({
  sections,
  loading,
  onChange,
}: {
  sections: HomepageSectionData[];
  loading: boolean;
  onChange: (s: HomepageSectionData[]) => void;
}) {
  const [forms, setForms] = useState<SectionFormState[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [allExams, setAllExams] = useState<AdminExam[]>([]);

  useEffect(() => {
    examsApi.getAll().then(({ data }) => {
      const items: any[] = Array.isArray(data) ? data : data.items ?? [];
      setAllExams(items.map((e: any) => ({
        id: e.id,
        name: e.name,
        icon: e.icon || '📚',
        bgGradient: e.bgGradient || 'from-emerald-500 to-teal-600',
        totalQuestions: e.totalQuestions || 0,
      })));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setForms(sections.map((s) => ({
      id: s.id,
      title: s.title,
      titleEn: s.titleEn ?? '',
      slug: s.slug,
      description: s.description ?? '',
      descriptionEn: s.descriptionEn ?? '',
      icon: s.icon ?? '',
      color: s.color ?? 'Orange',
      isVisible: s.isVisible,
      order: s.order,
      featuredExamIds: s.featuredExamIds ?? [],
    })));
  }, [sections]);

  const updateForm = (index: number, patch: Partial<SectionFormState>) =>
    setForms((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));

  const addNew = () =>
    setForms((prev) => [...prev, { ...BLANK_SECTION }]);

  const handleSave = async (index: number) => {
    const f = forms[index];
    if (!f.title.trim() || !f.slug.trim()) {
      toast.error('Le titre (FR) et l\'identifiant sont requis');
      return;
    }
    updateForm(index, { saving: true });
    try {
      const payload: HomepageSectionPayload = {
        title: f.title.trim(),
        titleEn: f.titleEn?.trim() || undefined,
        slug: f.slug.trim(),
        description: f.description?.trim() || undefined,
        descriptionEn: f.descriptionEn?.trim() || undefined,
        icon: f.icon?.trim() || undefined,
        color: f.color || undefined,
        isVisible: f.isVisible,
        order: f.order ?? 0,
        featuredExamIds: f.featuredExamIds ?? [],
      };
      if (f.id) {
        const { data } = await homepageSectionsApi.update(f.id, payload);
        onChange(sections.map((s) => (s.id === f.id ? data : s)));
        toast.success('Section mise à jour');
      } else {
        const { data } = await homepageSectionsApi.create(payload);
        onChange([...sections, data]);
        toast.success('Section créée');
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      updateForm(index, { saving: false });
    }
  };

  const handleDelete = async (index: number) => {
    const f = forms[index];
    if (!f.id) {
      setForms((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    if (!confirm('Supprimer cette section ?')) return;
    try {
      await homepageSectionsApi.delete(f.id);
      onChange(sections.filter((s) => s.id !== f.id));
      toast.success('Section supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleImportDone = async () => {
    setShowImport(false);
    try {
      const { data } = await homepageSectionsApi.getAll();
      onChange(data);
    } catch {}
  };

  const ic = 'w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-600">
            <LayoutDashboard size={20} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Sections de l'accueil</h2>
            <p className="text-xs text-gray-500">Gérez les sections affichées sur la page d'accueil.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg transition-colors"
          >
            <Upload size={15} />
            Importer
          </button>
          <button
            type="button"
            onClick={addNew}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={15} />
            Ajouter
          </button>
        </div>
      </div>

      {showImport && (
        <SectionsImportModal
          onClose={() => setShowImport(false)}
          onDone={handleImportDone}
        />
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      <div className="space-y-4">
        {forms.map((f, i) => (
          <div key={f.id ?? `new-${i}`} className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.isVisible}
                  onChange={(e) => updateForm(i, { isVisible: e.target.checked })}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Visible sur l'accueil</span>
              </label>
              <button
                type="button"
                onClick={() => handleDelete(i)}
                className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Titre (français) *</label>
                <input
                  type="text"
                  className={ic}
                  value={f.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    updateForm(i, { title, ...(!f.id && !f.slug ? { slug: slugify(title) } : {}) });
                  }}
                  placeholder="ex: Examens scolaires"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Titre (anglais)</label>
                <input
                  type="text"
                  className={ic}
                  value={f.titleEn ?? ''}
                  onChange={(e) => updateForm(i, { titleEn: e.target.value })}
                  placeholder="e.g. School exams"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Identifiant (URL) *</label>
              <input
                type="text"
                className={ic}
                value={f.slug}
                onChange={(e) => updateForm(i, { slug: e.target.value })}
                placeholder="ex: academic-exams"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description (français)</label>
                <textarea
                  className={`${ic} resize-none`}
                  rows={2}
                  value={f.description ?? ''}
                  onChange={(e) => updateForm(i, { description: e.target.value })}
                  placeholder="Description en français..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Description (anglais)</label>
                <textarea
                  className={`${ic} resize-none`}
                  rows={2}
                  value={f.descriptionEn ?? ''}
                  onChange={(e) => updateForm(i, { descriptionEn: e.target.value })}
                  placeholder="Description in English..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Icône (emoji)</label>
                <input
                  type="text"
                  className={ic}
                  value={f.icon ?? ''}
                  onChange={(e) => updateForm(i, { icon: e.target.value })}
                  placeholder="🎓"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Couleur</label>
                <select
                  className={ic}
                  value={f.color ?? 'Orange'}
                  onChange={(e) => updateForm(i, { color: e.target.value })}
                >
                  {COLOR_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Featured Exams Picker */}
            <FeaturedExamsPicker
              allExams={allExams}
              selectedIds={f.featuredExamIds ?? []}
              onChange={(ids) => updateForm(i, { featuredExamIds: ids })}
            />

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => handleSave(i)}
                disabled={f.saving}
                className="inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                <Save size={14} />
                {f.saving ? 'Enregistrement...' : f.id ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        ))}

        {!loading && forms.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-6">Aucune section. Cliquez sur "Ajouter" pour en créer une.</p>
        )}
      </div>
    </section>
  );
}

// ─── Featured Exams Picker ──────────────────────────────────────────────────

function FeaturedExamsPicker({
  allExams,
  selectedIds,
  onChange,
}: {
  allExams: AdminExam[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showPicker, setShowPicker] = useState(false);

  const selectedExams = selectedIds.map((id) => allExams.find((e) => e.id === id)).filter(Boolean) as AdminExam[];
  const available = allExams.filter(
    (e) => !selectedIds.includes(e.id) && e.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const addExam = (id: string) => onChange([...selectedIds, id]);
  const removeExam = (id: string) => onChange(selectedIds.filter((x) => x !== id));
  const moveUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...selectedIds];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx: number) => {
    if (idx >= selectedIds.length - 1) return;
    const next = [...selectedIds];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-xs font-medium text-gray-500">
          Examens à afficher sur l'accueil ({selectedIds.length}/6)
        </label>
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
        >
          <Plus size={13} />
          Ajouter
        </button>
      </div>

      {/* Selected exams list */}
      {selectedExams.length > 0 ? (
        <div className="space-y-1.5 mb-3">
          {selectedExams.map((exam, idx) => (
            <div
              key={exam.id}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-900/40 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div className="flex flex-col gap-0.5">
                <button
                  type="button"
                  onClick={() => moveUp(idx)}
                  disabled={idx === 0}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-20"
                >
                  <GripVertical size={10} />
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(idx)}
                  disabled={idx === selectedExams.length - 1}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-20"
                >
                  <GripVertical size={10} />
                </button>
              </div>
              <span className="text-sm mr-1">{exam.icon}</span>
              <span className="text-sm text-gray-900 dark:text-white font-medium flex-1 truncate">{exam.name}</span>
              <span className="text-xs text-gray-400">{exam.totalQuestions} Q</span>
              <button
                type="button"
                onClick={() => removeExam(exam.id)}
                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400 mb-3 italic">
          Aucun examen sélectionné — tous les examens de cette section seront affichés automatiquement.
        </p>
      )}

      {/* Picker dropdown */}
      {showPicker && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden mb-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un examen..."
              className="w-full pl-9 pr-3 py-2.5 bg-gray-50 dark:bg-gray-900/40 text-sm text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-0"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {available.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">Aucun examen trouvé</p>
            ) : (
              available.slice(0, 20).map((exam) => (
                <button
                  key={exam.id}
                  type="button"
                  onClick={() => {
                    addExam(exam.id);
                    if (selectedIds.length >= 5) setShowPicker(false);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 text-left transition-colors"
                >
                  <span className="text-sm">{exam.icon}</span>
                  <span className="text-sm text-gray-900 dark:text-white flex-1 truncate">{exam.name}</span>
                  <span className="text-xs text-gray-400">{exam.totalQuestions} Q</span>
                  <Plus size={14} className="text-emerald-500 flex-shrink-0" />
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sections Import Modal ──────────────────────────────────────────────────

type ImportStep = 'upload' | 'preview' | 'done';

interface PreviewData {
  totalRows: number;
  validCount: number;
  invalidCount: number;
  rows: { rowNumber: number; raw: Record<string, string>; errors: string[]; dto?: Record<string, unknown> }[];
}

interface CommitResult {
  totalRows: number;
  importedCount: number;
  skippedCount: number;
}

function SectionsImportModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (f: File) => {
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Seuls les fichiers .xlsx sont acceptés');
      return;
    }
    setFile(f);
    setLoading(true);
    try {
      const { data } = await homepageSectionsImportApi.preview(f);
      setPreview(data);
      setStep('preview');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'analyse du fichier');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCommit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const { data } = await homepageSectionsImportApi.commit(file);
      setCommitResult(data);
      setStep('done');
      toast.success(`${data.importedCount} section(s) importée(s)`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-600">
              <FileSpreadsheet size={18} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Importer des sections</h3>
              <p className="text-xs text-gray-500">
                {step === 'upload' && 'Uploadez un fichier Excel (.xlsx)'}
                {step === 'preview' && 'Vérifiez les données avant l\'import'}
                {step === 'done' && 'Import terminé'}
              </p>
            </div>
          </div>
          <button onClick={step === 'done' ? onDone : onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'upload' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => homepageSectionsImportApi.downloadTemplate('fr')}
                className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                <Download size={15} />
                Télécharger le modèle Excel
              </button>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400'
                }`}
              >
                <Upload size={32} className="mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                  Glissez-déposez votre fichier ici
                </p>
                <p className="text-xs text-gray-400 mt-1">ou cliquez pour parcourir (.xlsx, max 5 Mo)</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
              </div>

              {loading && (
                <div className="flex items-center justify-center gap-2 py-4">
                  <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm text-gray-500">Analyse en cours...</span>
                </div>
              )}
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{preview.totalRows}</p>
                  <p className="text-xs text-gray-500">Total</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{preview.validCount}</p>
                  <p className="text-xs text-gray-500">Valides</p>
                </div>
                <div className={`rounded-lg p-3 text-center ${preview.invalidCount > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-900/40'}`}>
                  <p className={`text-2xl font-bold ${preview.invalidCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{preview.invalidCount}</p>
                  <p className="text-xs text-gray-500">Invalides</p>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                      <th className="px-3 py-2 text-xs font-medium text-gray-500">#</th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500">Titre</th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500">Slug</th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500">Couleur</th>
                      <th className="px-3 py-2 text-xs font-medium text-gray-500">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {preview.rows.map((r) => (
                      <tr key={r.rowNumber} className={r.errors.length > 0 ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                        <td className="px-3 py-2 text-gray-400">{r.rowNumber}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white font-medium truncate max-w-[200px]">{r.raw.title || '—'}</td>
                        <td className="px-3 py-2 text-gray-500 font-mono text-xs truncate max-w-[160px]">{r.raw.slug || r.dto?.slug as string || '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{r.raw.color || '—'}</td>
                        <td className="px-3 py-2">
                          {r.errors.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
                              <CheckCircle size={13} /> OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-red-500 text-xs" title={r.errors.join('; ')}>
                              <AlertCircle size={13} /> {r.errors[0]}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === 'done' && commitResult && (
            <div className="text-center py-6 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle size={28} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {commitResult.importedCount} section(s) importée(s)
                </p>
                {commitResult.skippedCount > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {commitResult.skippedCount} ligne(s) ignorée(s) (doublons ou erreurs)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          {step === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => { setStep('upload'); setFile(null); setPreview(null); }}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Retour
              </button>
              <button
                type="button"
                onClick={handleCommit}
                disabled={loading || !preview || preview.validCount === 0}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload size={15} />
                    Importer {preview?.validCount} section(s)
                  </>
                )}
              </button>
            </>
          )}
          {step === 'done' && (
            <button
              type="button"
              onClick={onDone}
              className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <CheckCircle size={15} />
              Fermer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function LimitRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
      <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
      <span className="text-sm font-semibold text-gray-900 dark:text-white">{value}</span>
    </div>
  );
}

function LimitInput({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (raw: string) => void;
  min: number;
  max: number;
}) {
  return (
    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 rounded-lg flex items-center justify-between gap-3">
      <label className="text-sm text-gray-600 dark:text-gray-400 flex-1">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-24 px-2 py-1.5 text-sm text-right font-semibold bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
      />
    </div>
  );
}
